<template>
  <div class="box-border w-full bg-white border border-black/[0.08] shadow-none rounded-[14px] p-4 sm:p-5 flex flex-col gap-4 relative overflow-hidden">
    <div
      v-if="!hideTitle"
      class="box-border w-full h-fit shrink-0 flex flex-row justify-start items-center"
    >
      <div class="text-[16px]/[22px] sm:text-[17px]/[22px] box-border text-[#043f42ff] font-[Raleway,system-ui,sans-serif] font-bold text-left whitespace-nowrap">
        Signature Progress
      </div>
    </div>
    <div class="box-border w-full h-fit shrink-0 flex flex-col gap-[15px] justify-start items-start">
      <div class="box-border w-full h-fit shrink-0 flex flex-col gap-[6px] justify-start items-start">
        <div class="text-[12px]/[16px] box-border w-full shrink-0 text-[#5b5c60ff] font-[Raleway,system-ui,sans-serif] font-normal text-left">
          Training Provider
        </div>
        <div class="box-border w-full h-fit shrink-0 flex flex-row gap-[8px] justify-start items-center flex-wrap">
          <div
            class="box-border w-fit shrink-0 h-fit flex flex-row gap-[5px] px-[8px] py-[3px] justify-center items-center rounded-full"
            :class="statusColor(providerProgress, total, providerStarted)"
          >
            <div
              class="box-border w-[6px] shrink-0 h-[6px] rounded-full overflow-hidden"
              :class="dotColor(providerProgress, total, providerStarted)"
            />
            <div class="text-[11px]/[15px] box-border text-[#000000ff] font-[Raleway,system-ui,sans-serif] font-normal text-left whitespace-nowrap">
              {{ statusText(providerProgress, total, providerStarted) }}
            </div>
          </div>
          <div class="text-[11px]/[15px] sm:text-[12px]/[16px] box-border text-[#5b5c60ff] font-[Raleway,system-ui,sans-serif] font-medium text-left whitespace-nowrap">
            {{ providerProgress }} of {{ total }}
          </div>
        </div>
      </div>
      <div class="box-border w-full h-fit shrink-0 flex flex-col gap-[6px] justify-start items-start">
        <div class="text-[12px]/[16px] box-border w-full shrink-0 text-[#5b5c60ff] font-[Raleway,system-ui,sans-serif] font-normal text-left">
          Learner
        </div>
        <div class="box-border w-full h-fit shrink-0 flex flex-row gap-[8px] justify-start items-center flex-wrap">
          <div
            class="box-border w-fit shrink-0 h-fit flex flex-row gap-[5px] px-[8px] py-[3px] justify-center items-center rounded-full"
            :class="statusColor(learnerProgress, total, learnerStarted)"
          >
            <div
              class="box-border w-[6px] shrink-0 h-[6px] rounded-full overflow-hidden"
              :class="dotColor(learnerProgress, total, learnerStarted)"
            />
            <div class="text-[11px]/[15px] box-border text-[#000000ff] font-[Raleway,system-ui,sans-serif] font-normal text-left whitespace-nowrap">
              {{ statusText(learnerProgress, total, learnerStarted) }}
            </div>
          </div>
          <div class="text-[11px]/[15px] sm:text-[12px]/[16px] box-border text-[#5b5c60ff] font-[Raleway,system-ui,sans-serif] font-medium text-left whitespace-nowrap">
            {{ learnerProgress }} of {{ total }}
          </div>
        </div>
      </div>
      <div class="box-border w-full h-fit shrink-0 flex flex-col gap-[6px] justify-start items-start">
        <div class="text-[11px]/[19px] box-border w-full min-h-[19px] shrink-0 text-[#5b5c60ff] font-[Raleway,system-ui,sans-serif] font-normal text-left flex items-center justify-start gap-[8px]">
          <template v-if="isEditingSponsorEmail">
            <div
              class="flex items-center gap-[4px] w-full"
              @click.stop
            >
              <input
                v-model="editSponsorEmailValue"
                type="email"
                class="border rounded px-1 text-[10px] w-full outline-none focus:ring-1 focus:ring-[#043f42] h-[19px] bg-white text-black"
                @keyup.enter="saveSponsorEmail"
                @keyup.esc="cancelEditSponsorEmail"
              >
              <button
                class="text-[#01d680] hover:text-[#043f42] shrink-0"
                title="Save"
                @click="saveSponsorEmail"
              >
                <svg
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="2"
                  stroke="currentColor"
                  class="w-3 h-3"
                ><path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M4.5 12.75l6 6 9-13.5"
                /></svg>
              </button>
              <button
                class="text-red-500 hover:text-red-700 shrink-0"
                title="Cancel"
                @click="cancelEditSponsorEmail"
              >
                <svg
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="2"
                  stroke="currentColor"
                  class="w-3 h-3"
                ><path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                /></svg>
              </button>
            </div>
          </template>
          <template v-else>
            <span class="truncate">Sponsor</span>
            <div class="flex items-center gap-[8px]">
              <div
                class="box-border w-[12px] h-[12px] shrink-0 text-[#888888ff] hover:text-black cursor-pointer"
                title="Resend to sponsor"
                @click="resendSponsor"
              >
                <svg
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="2"
                  stroke="currentColor"
                  class="w-full h-full"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
                  />
                </svg>
              </div>
              <div
                class="box-border w-[12px] h-[12px] shrink-0 text-[#888888ff] hover:text-black cursor-pointer"
                title="Edit sponsor email"
                @click="startEditSponsorEmail"
              >
                <svg
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="2"
                  stroke="currentColor"
                  class="w-full h-full"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125"
                  />
                </svg>
              </div>
            </div>
          </template>
        </div>
        <div class="box-border w-full h-fit shrink-0 flex flex-row gap-[8px] justify-start items-center flex-wrap">
          <div
            class="box-border w-fit shrink-0 h-fit flex flex-row gap-[5px] px-[8px] py-[3px] justify-center items-center rounded-full"
            :class="sponsorStatusColor"
          >
            <div
              class="box-border w-[6px] shrink-0 h-[6px] rounded-full overflow-hidden"
              :class="sponsorDotColor"
            />
            <div class="text-[11px]/[15px] box-border text-[#000000ff] font-[Raleway,system-ui,sans-serif] font-normal text-left whitespace-nowrap">
              {{ sponsorStatusText }}
            </div>
          </div>
          <div class="text-[11px]/[15px] sm:text-[12px]/[16px] box-border text-[#5b5c60ff] font-[Raleway,system-ui,sans-serif] font-medium text-left whitespace-nowrap">
            {{ sponsorProgress }} of {{ total }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
  programme: {
    type: Object,
    required: true
  },
  enrollments: {
    type: Array,
    default: () => []
  },
  hideTitle: {
    type: Boolean,
    default: false
  }
})

const isEditingSponsorEmail = ref(false)
const editSponsorEmailValue = ref('')

const startEditSponsorEmail = () => {
  editSponsorEmailValue.value = props.programme.sponsor_email || ''
  isEditingSponsorEmail.value = true
}

const cancelEditSponsorEmail = () => {
  isEditingSponsorEmail.value = false
  editSponsorEmailValue.value = ''
}

const saveSponsorEmail = () => {
  if (!editSponsorEmailValue.value || editSponsorEmailValue.value.trim() === '') {
    const event = new CustomEvent('programme-action', {
      detail: { action: 'toast', message: 'Sponsor email cannot be empty', type: 'error' },
      bubbles: true,
      composed: true
    })
    window.dispatchEvent(event)
    return
  }

  const event = new CustomEvent('programme-action', {
    detail: { action: 'update_sponsor_email', email: editSponsorEmailValue.value },
    bubbles: true,
    composed: true
  })
  window.dispatchEvent(event)
  isEditingSponsorEmail.value = false
}

const resendSponsor = () => {
  if (!props.programme.sponsor_email) {
    const event = new CustomEvent('programme-action', {
      detail: { action: 'toast', message: 'Sponsor email is missing', type: 'error' },
      bubbles: true,
      composed: true
    })
    window.dispatchEvent(event)
    return
  }

  if (confirm('Are you sure you want to resend the signature request to the sponsor?')) {
    const event = new CustomEvent('programme-action', {
      detail: { action: 'dispatch_sponsor' },
      bubbles: true,
      composed: true
    })
    window.dispatchEvent(event)
  }
}

const total = computed(() => {
  return props.enrollments.filter(e => {
    const submitters = e.submission?.submitters || []
    return !submitters.some(s => s.declined_at)
  }).length
})

const isProviderSubmitter = (s) => {
  if (!s) return false
  const uuids = props.programme.field_mapping?.trainer || ['trainer']
  return uuids.includes(s.uuid) || s.name?.toLowerCase() === 'trainer' || s.uuid?.toLowerCase().includes('trainer')
}

const isLearnerSubmitter = (s, e) => {
  if (!s) return false
  const uuids = props.programme.field_mapping?.student || ['student']
  return s.email === e?.student_email || uuids.includes(s.uuid) || s.name?.toLowerCase() === 'student' || s.uuid?.toLowerCase().includes('student')
}

const isSponsorSubmitter = (s) => {
  if (!s) return false
  const uuids = props.programme.field_mapping?.sponsor || ['sponsor_0']
  if (uuids.includes(s.uuid)) return true
  if (s.uuid?.toLowerCase().includes('sponsor')) return true
  if (s.name?.toLowerCase() === 'sponsor') return true
  if (props.programme.sponsor_email && s.email && s.email.toLowerCase() === props.programme.sponsor_email.toLowerCase()) return true
  return false
}

const providerProgress = computed(() => {
  return props.enrollments.filter(e => {
    const submitters = e.submission?.submitters || []
    return submitters.some(s => isProviderSubmitter(s) && s.completed_at)
  }).length
})

const learnerProgress = computed(() => {
  return props.enrollments.filter(e => {
    const submitters = e.submission?.submitters || []
    return submitters.some(s => isLearnerSubmitter(s, e) && s.completed_at)
  }).length
})

const sponsorProgress = computed(() => {
  return props.enrollments.filter(e => {
    const submitters = e.submission?.submitters || []
    return submitters.some(s => isSponsorSubmitter(s) && s.completed_at)
  }).length
})

const providerStarted = computed(() => {
  return props.enrollments.some(e => {
    const submitters = e.submission?.submitters || []
    return submitters.some(s => isProviderSubmitter(s) && (s.started_at || s.completed_at))
  })
})

const learnerStarted = computed(() => {
  return props.enrollments.some(e => {
    const submitters = e.submission?.submitters || []
    return submitters.some(s => isLearnerSubmitter(s, e) && (s.started_at || s.completed_at))
  })
})

const sponsorStarted = computed(() => {
  return props.enrollments.some(e => {
    const submitters = e.submission?.submitters || []
    return submitters.some(s => isSponsorSubmitter(s) && (s.started_at || s.completed_at))
  })
})

const sponsorStatus = computed(() => {
  if (props.programme.cohort?.sponsor_completed_at || (total.value > 0 && sponsorProgress.value === total.value)) {
    return 'completed'
  }
  if (sponsorProgress.value > 0 || sponsorStarted.value || props.programme.sponsor_status === 'in_progress') {
    return 'in_progress'
  }
  if (props.programme.sponsor_status === 'opened') {
    return 'opened'
  }
  const hasOpenedSubmitter = props.enrollments.some(e => {
    const submitters = e.submission?.submitters || []
    return submitters.some(s => isSponsorSubmitter(s) && s.opened_at)
  })
  if (hasOpenedSubmitter) {
    return 'opened'
  }
  if (props.programme.sponsor_status === 'delivered' || props.programme.sponsor_dispatched_at) {
    return 'delivered'
  }
  const hasSentSubmitter = props.enrollments.some(e => {
    const submitters = e.submission?.submitters || []
    return submitters.some(s => isSponsorSubmitter(s) && s.sent_at)
  })
  if (hasSentSubmitter) {
    return 'delivered'
  }
  return 'not_complete'
})

const sponsorStatusText = computed(() => {
  switch (sponsorStatus.value) {
    case 'completed':
      return 'Completed'
    case 'in_progress':
      return 'In progress'
    case 'opened':
      return 'Opened'
    case 'delivered':
      return 'Delivered'
    default:
      return 'Not complete'
  }
})

const sponsorStatusColor = computed(() => {
  if (total.value === 0) return 'bg-[#c9c9c9ff]'
  switch (sponsorStatus.value) {
    case 'completed':
      return 'bg-[#01d680ff]'
    case 'in_progress':
      return 'bg-[#ffea8a]'
    case 'opened':
      return 'bg-[#bae6fd]'
    case 'delivered':
      return 'bg-[#f3e8ff]'
    default:
      return 'bg-[#ff8e8eff]'
  }
})

const sponsorDotColor = computed(() => {
  if (total.value === 0) return 'bg-[#1c252eff]'
  switch (sponsorStatus.value) {
    case 'completed':
      return 'bg-[#043f42ff]'
    case 'in_progress':
      return 'bg-[#b8860b]'
    case 'opened':
      return 'bg-[#0284c7]'
    case 'delivered':
      return 'bg-[#9333ea]'
    default:
      return 'bg-[#b80000ff]'
  }
})

const statusColor = (prog, tot, hasStarted) => {
  if (tot === 0) return 'bg-[#c9c9c9ff]'
  if (prog === tot) return 'bg-[#01d680ff]'
  if (prog > 0 || hasStarted) return 'bg-[#ffea8a]'
  return 'bg-[#ff8e8eff]'
}

const dotColor = (prog, tot, hasStarted) => {
  if (tot === 0) return 'bg-[#1c252eff]'
  if (prog === tot) return 'bg-[#043f42ff]'
  if (prog > 0 || hasStarted) return 'bg-[#b8860b]'
  return 'bg-[#b80000ff]'
}

const statusText = (prog, tot, hasStarted) => {
  if (tot === 0) return 'Not complete'
  if (prog === tot) return 'Completed'
  if (prog > 0 || hasStarted) return 'In progress'
  return 'Not complete'
}
</script>
